import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { filterWalletTransactions } from "../../../../../redux/walletTransaction/walletTransactionActions";
import Empty from "../../../../../assets/images/emptyState/payment.svg";
import Success from "../../../../../assets/images/icons/success-txn.svg";
import Pending from "../../../../../assets/images/icons/pending-txn.svg";
import Fail from "../../../../../assets/images/icons/failed-txn.svg";
import { getUserFromStorage } from "../../../../../utils/Auth";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";


export default function AllTransactions({ selectedWallet }) {
    const dispatch = useDispatch();
    const { walletTransactions: transactions, loading } = useSelector((state) => state.walletTransaction);
    const user = getUserFromStorage();

    const userId = user?.id;
    const walletId = selectedWallet?.id;

    useEffect(() => {
        if (walletId && userId) {
            dispatch(
                filterWalletTransactions({
                    user_id: userId,
                    wallet_id: walletId,
                })
            );
        }
    }, [walletId, userId, dispatch]);

    const groupTransactionsByMonth = (transactions) => {
        const grouped = {};
        transactions.forEach((transaction) => {
            const date = new Date(transaction.created_at);
            const monthYear = date.toLocaleString("en-US", {
                month: "long",
                year: "numeric",
            });
            if (!grouped[monthYear]) {
                grouped[monthYear] = [];
            }
            grouped[monthYear].push(transaction);
        });
        return grouped;
    };

    const formatTime = (dateString) => {
        const date = new Date(dateString);
        return date.toLocaleTimeString("en-US", {
            hour: "2-digit",
            minute: "2-digit",
            hour12: false,
        });
    };

    const formatAmount = (amount, currencyCode) => {
        return `${currencyCode}${parseFloat(amount).toLocaleString("en-US", {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        })}`;
    };

    if (loading) {
        return (
            <div className="space-y-4">
                {[...Array(3)].map((_, i) => (
                    <div key={i} className="py-3 px-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
                        <div className="flex items-center justify-between">
                            <Skeleton width={80} height={20} />
                            <Skeleton width={80} height={20} />
                            <Skeleton width={80} height={20} />
                            <Skeleton width={80} height={20} />
                        </div>
                    </div>
                ))}
            </div>
        );
    }

    if (!transactions || transactions.length === 0) {
        return (
            <div className="my-10 text-center">
                <img
                    src={Empty}
                    alt="empty state"
                    className="flex justify-center mx-auto mb-4 w-32 h-32"
                />
                <p className="font-bold mb-2 text-gray-900 dark:text-white">No Transactions</p>
                <p className="text-sm text-gray-400">
                    You don't have any transactions yet.
                </p>
            </div>
        );
    }

    const groupedTransactions = groupTransactionsByMonth(transactions);

    return (
        <div className="space-y-6">
            {Object.entries(groupedTransactions).map(([monthYear, transactions]) => (
                <div key={monthYear}>
                    <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-3">
                        {monthYear}
                    </h3>
                    <div className="space-y-2">
                        {transactions.map((transaction) => (
                            <div
                                key={transaction.id}
                                className="flex items-center gap-2 sm:gap-4 py-3 border-b border-[#9CA3AF]"
                            >
                                <div className="w-16 sm:flex-1">
                                    <p className="text-xs sm:text-sm font-medium text-[#BCBCBC] dark:text-white capitalize truncate">
                                        {transaction.type || "Transaction"}
                                    </p>
                                </div>
                                <div className="flex-shrink-0 sm:flex-1">
                                    <p className="text-xs sm:text-sm text-[#676565] dark:text-[#BCBCBC] whitespace-nowrap px-1">
                                        {formatTime(transaction.created_at)}
                                    </p>
                                </div>
                                <div className="flex-shrink-0 sm:flex-1">
                                    <span className="flex items-center gap-1 text-xs sm:text-sm font-medium whitespace-nowrap">
                                        {transaction.status?.toLowerCase() === "pending" ? (
                                            <>
                                                <img src={Pending} alt="icon" className="w-4 h-4 sm:w-5 sm:h-5 flex-shrink-0" />
                                                <span className="capitalize text-[#FFA927]">{transaction.status}</span>
                                            </>
                                        ) : transaction.status?.toLowerCase() === "successful" ||
                                          transaction.status?.toLowerCase() === "success" ? (
                                            <>
                                                <img src={Success} alt="icon" className="w-4 h-4 sm:w-5 sm:h-5 flex-shrink-0" />
                                                <span className="capitalize text-[#00D220]">{transaction.status}</span>
                                            </>
                                        ) : (
                                            <>
                                                <img src={Fail} alt="icon" className="w-4 h-4 sm:w-5 sm:h-5 flex-shrink-0" />
                                                <span className="capitalize text-primary">{transaction.status}</span>
                                            </>
                                        )}
                                    </span>
                                </div>
                                <div className="flex-1 text-right">
                                    <p className="text-xs sm:text-sm font-bold text-[#676565] dark:text-[#BCBCBC] whitespace-nowrap">
                                        {formatAmount(transaction.amount, selectedWallet?.currency_code)}
                                    </p>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            ))}
        </div>
    );
}
